import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button, type ButtonVariant } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AlertCircle, HelpCircle, Trash } from 'lucide-react'
import { useState, type MouseEvent, type ReactNode } from 'react'
import LoadingButton from '@/components/ui/loading-button'

export type ConfirmationDialogVariant = 'destructive' | 'question' | 'default' | 'ghost'

type ConfirmationDialogVariantConfig = {
  icon?: ReactNode
  buttonVariant: ButtonVariant
  actionClassName: string
  confirmText?: string
  pendingText?: string
}

const variantConfig: Record<ConfirmationDialogVariant, ConfirmationDialogVariantConfig> = {
  destructive: {
    icon: <Trash />,
    buttonVariant: 'destructive',
    actionClassName: 'bg-destructive hover:bg-destructive/90',
    confirmText: 'Delete',
    pendingText: 'Deleting...',
  },
  question: {
    icon: <HelpCircle />,
    buttonVariant: 'default',
    actionClassName: '',
  },
  default: {
    icon: <AlertCircle />,
    buttonVariant: 'default',
    actionClassName: '',
  },
  ghost: {
    buttonVariant: 'ghost',
    actionClassName: '',
  },
}

export type ConfirmationDialogProps = {
  title: string
  description?: ReactNode
  descriptionClassName?: string
  children?: ReactNode
  icon?: ReactNode
  /** Return `false` (or resolve to it) to keep the dialog open. */
  onConfirm?: (event: MouseEvent<HTMLButtonElement>) => boolean | void | Promise<boolean | void>
  noButtonText?: boolean
  noCancel?: boolean
  isPending?: boolean
  /** `null` renders no trigger; omit it for the default variant button. */
  trigger?: ReactNode
  className?: string
  variant?: ConfirmationDialogVariant
  confirmText?: ReactNode
  confirmDisabled?: boolean
  cancelText?: ReactNode
  pendingText?: ReactNode
  onOpenChange?: (open: boolean) => void
  open?: boolean
  closeOnError?: boolean
}

export function ConfirmationDialog({
  title,
  description,
  descriptionClassName,
  children,
  icon,
  onConfirm,
  noButtonText = false,
  noCancel = false,
  isPending = false,
  trigger = undefined,
  className = '',
  variant = 'default',
  confirmText,
  confirmDisabled = false,
  cancelText = 'Cancel',
  pendingText,
  onOpenChange,
  open,
  closeOnError = true,
}: ConfirmationDialogProps) {
  const config = variantConfig[variant]
  const dialogIcon = icon ?? config.icon
  const [localOpen, setLocalOpen] = useState(false)

  const isOpen = open ?? localOpen
  const setIsOpen = onOpenChange ?? setLocalOpen
  const handleOpenChange = (nextOpen: boolean) => {
    if (!isPending || nextOpen) setIsOpen(nextOpen)
  }

  const handleConfirm = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.preventDefault()
      const shouldClose = await Promise.resolve(onConfirm?.(e))
      if (shouldClose !== false) {
        setIsOpen(false)
      }
    } catch (error) {
      console.error(error)
      if (closeOnError) {
        setIsOpen(false)
      }
    }
  }

  return (
    <AlertDialog onOpenChange={handleOpenChange} open={isOpen}>
      {trigger !== null ? (
        <AlertDialogTrigger asChild>
          {trigger || (
            <Button
              variant={config.buttonVariant}
              size="sm"
              className={className}
              aria-label={noButtonText ? title : undefined}
            >
              <div className="inline-flex items-center gap-2">
                {dialogIcon && <span>{dialogIcon}</span>}
                {!noButtonText && <span>{title}</span>}
              </div>
            </Button>
          )}
        </AlertDialogTrigger>
      ) : null}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="inline-flex items-center gap-2">
            {dialogIcon && <span>{dialogIcon}</span>}
            {title}
          </AlertDialogTitle>
          {description ? (
            <AlertDialogDescription className={descriptionClassName}>
              {description}
            </AlertDialogDescription>
          ) : null}
          {children}
        </AlertDialogHeader>
        <AlertDialogFooter>
          {!noCancel && <AlertDialogCancel disabled={isPending}>{cancelText}</AlertDialogCancel>}
          <AlertDialogAction asChild>
            <LoadingButton
              disabled={isPending || confirmDisabled}
              isPending={isPending}
              onClick={handleConfirm}
              className={cn(
                variant === 'destructive' && 'text-destructive-foreground',
                config.actionClassName
              )}
            >
              {isPending
                ? (pendingText ?? config.pendingText ?? 'Processing...')
                : (confirmText ?? config.confirmText ?? 'Confirm')}
            </LoadingButton>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
